import api from "@/lib/axiosConfig";
import { Document, FetchedDocument } from "@/lib/types";
import { parseJsonOrUndefined } from "@/lib/utils";
import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { RelativePosition } from "../dtos";

interface DocumentDTO {
  where: any;
  order: any;
  take: number;
  skip: number;
}

export const baseRoute = "back/api/documents";

export const mapFetchedDocument = (fd: FetchedDocument): Document => ({
  ...fd,
  //TECH_DEBT
  documentsDescriptionDimensions: parseJsonOrUndefined(
    fd.documentsDescriptionDimensions,
  ),
});

async function getDocuments({
  where,
  order,
  take,
  skip,
}: DocumentDTO): Promise<Document[]> {
  const { data } = await api.post<FetchedDocument[]>(
    `back/api/documents?take=${take || 10}&skip=${skip || 0}`,
    {
      where,
      orderBy: order,
    },
  );
  return data.map(mapFetchedDocument);
}

export const useGetAllDocuments = ({
  where,
  order,
  take,
  skip,
}: DocumentDTO) => {
  return useQuery({
    queryKey: ["documents", where, order, take, skip],
    queryFn: () => getDocuments({ where, order, take, skip }),
    placeholderData: keepPreviousData,
  });
};

export async function getCountByStatus(where?: object) {
  const { data } = await api.post(`back/api/documents/countByStatus`, {
    where,
  });
  return data;
}

export const useCountByStatus = () => {
  return useQuery({
    queryKey: ["countByStatus", "document"],
    queryFn: () => getCountByStatus(),
  });
};

export const getRelativePosition = async (
  id: string,
  order: object,
  where: object,
) => {
  const { data } = await api.post<RelativePosition>(
    `${baseRoute}/${id}/position`,
    {
      order,
      where,
    },
  );
  return data;
};

const getDocument = async (id: string): Promise<Document> => {
  const { data } = await api.get<FetchedDocument>(`back/api/documents/${id}`);
  return mapFetchedDocument(data);
};

export const useGetDocument = (id?: string) => {
  return useQuery({
    queryKey: ["document", id],
    queryFn: () => (id ? getDocument(id) : undefined),
  });
};

async function updateDocument(document: Document) {
  const {
    instanceId: _iid,
    updatedById: _ubid,
    createdById: _cbid,
    ...rest
  } = document;
  return await api.patch(`back/api/documents/${document.id}`, {
    data: {
      ...rest,
      documentsDescriptionDimensions: JSON.stringify(
        rest.documentsDescriptionDimensions,
      ),
    },
  });
}

export function useUpdateDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateDocument,
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: ["document", v.id] });
      queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

type CreateDocumentParams = {
  documentsDescriptionNumeroEnregistrement: string;
  documentsDescriptionTitre: string;
};
type CreateDocumentResponse = Pick<Document, "id">;

const createDocument = async ({
  documentsDescriptionNumeroEnregistrement,
  documentsDescriptionTitre,
}: CreateDocumentParams) => {
  return await api
    .post<CreateDocumentResponse>("back/api/documents/create", {
      documentsDescriptionNumeroEnregistrement,
      documentsDescriptionTitre,
    })
    .then((res) => res.data);
};

export const useCreateDocument = (
  options?: UseMutationOptions<
    CreateDocumentResponse,
    Error,
    CreateDocumentParams
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: createDocument,
    onSuccess: (data, vars, ctx) => {
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      queryClient.invalidateQueries({ queryKey: ["document", data.id] });
      queryClient.invalidateQueries({
        queryKey: ["countByStatus", "document"],
      });
      queryClient.invalidateQueries({ queryKey: ["instance", "count-data"] });
      options?.onSuccess?.(data, vars, ctx);
    },
  });
};
